import { useEffect, useMemo, useState } from "react";
import { getUrl } from "aws-amplify/storage";
import { reportAppError, reportPerformance } from "./observability";

const INITIAL_STATE = { key: "", url: null, error: null, status: "idle" };
const CACHE_SKEW_MS = 60 * 1000;
const signedUrlCache = new Map();

function isBrowserUatContext() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    process.env.NODE_ENV === "development" ||
    host === "windroos.boss-tech.nl" ||
    host === "localhost" ||
    host === "127.0.0.1"
  );
}

function safeErrorDetails(error) {
  if (!error) return {};
  return {
    name: error.name,
    message: error.message,
    code: error.code || error.$metadata?.httpStatusCode,
  };
}

function logSignedUrl(level, message, details) {
  if (!isBrowserUatContext()) return;
  const logger = level === "error" ? console.error : console.info;
  const key = details?.s3Key ? ` (${details.s3Key})` : "";
  logger(`[Windroos media] ${message}${key}`, details);
}

export function isAbsoluteSignedMediaUrl(value) {
  return /^https?:\/\//i.test(value || "");
}

export function normalizeSignedMediaKey(value) {
  const key = typeof value === "string" ? value.trim() : "";
  if (!key || isAbsoluteSignedMediaUrl(key)) return key;
  return key.replace(/^s3:\/\/[^/]+\//i, "").replace(/^\/+/, "");
}

function mediaCategory(key) {
  if (/\.(avif|gif|jpe?g|png|svg|webp)$/i.test(key)) return "image";
  if (/\.(m4v|mov|mp4|ogg|webm)$/i.test(key)) return "video";
  if (/\.pdf$/i.test(key)) return "document";
  return "asset";
}

function cachedGetUrl(key) {
  const now = Date.now();
  const cached = signedUrlCache.get(key);

  if (cached?.url && (!cached.expiresAt || cached.expiresAt - CACHE_SKEW_MS > now)) {
    return Promise.resolve({
      url: cached.url,
      expiresAt: cached.expiresAt ? new Date(cached.expiresAt) : undefined,
    });
  }

  if (cached?.promise) return cached.promise;

  const promise = getUrl({ path: key, options: { validateObjectExistence: false } })
    .then((result) => {
      const signedUrl = result.url.toString();
      const expiresAt = result.expiresAt?.getTime?.() || 0;
      signedUrlCache.set(key, { url: signedUrl, expiresAt });
      return { url: signedUrl, expiresAt: result.expiresAt };
    })
    .catch((err) => {
      signedUrlCache.delete(key);
      throw err;
    });

  signedUrlCache.set(key, { promise });
  return promise;
}

export async function resolveSignedMediaUrl(s3Key) {
  const key = normalizeSignedMediaKey(s3Key);

  if (!key) {
    return { key, url: null, expiresAt: undefined };
  }

  if (isAbsoluteSignedMediaUrl(key)) {
    return { key, url: key, expiresAt: undefined };
  }

  const result = await cachedGetUrl(key);
  return { key, url: result.url, expiresAt: result.expiresAt };
}

export default function useSignedUrl(s3Key) {
  const key = useMemo(() => normalizeSignedMediaKey(s3Key), [s3Key]);
  const [state, setState] = useState(INITIAL_STATE);

  useEffect(() => {
    let cancelled = false;

    if (!key) {
      setState(INITIAL_STATE);
      return undefined;
    }

    if (isAbsoluteSignedMediaUrl(key)) {
      setState({ key, url: key, error: null, status: "ready" });
      return undefined;
    }

    setState({ key, url: null, error: null, status: "loading" });
    logSignedUrl("info", "signed URL requested", { s3Key: key });
    const startedAt = performance.now();

    resolveSignedMediaUrl(key)
      .then((result) => {
        reportPerformance({
          stage: "signed_url",
          durationMs: performance.now() - startedAt,
          mediaCategory: mediaCategory(key),
        });
        if (cancelled) return;
        setState({ key, url: result.url, error: null, status: "ready" });
        logSignedUrl("info", "signed URL ready", {
          s3Key: key,
          expiresAt: result.expiresAt?.toISOString?.(),
        });
      })
      .catch((err) => {
        reportAppError({
          stage: "signed_url",
          error: err,
          durationMs: performance.now() - startedAt,
          mediaCategory: mediaCategory(key),
        });
        if (cancelled) return;
        setState({ key, url: null, error: err, status: "error" });
        logSignedUrl("error", "signed URL failed", {
          s3Key: key,
          error: safeErrorDetails(err),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  if (!key) return INITIAL_STATE;

  if (isAbsoluteSignedMediaUrl(key)) {
    return { key, url: key, error: null, status: "ready" };
  }

  if (state.key !== key) {
    return { key, url: null, error: null, status: "loading" };
  }

  return state;
}
